import {
  generateAWeekRangeForPicker,
  INITIAL_DATE,
} from 'constants/calendarConstants';
import {halfValueOfArray} from 'helpers/halfValueOfArray';
import {makeAutoObservable} from 'mobx';
import ClassApiManager from 'services/API/ClassApi/ClassApiManager';
import {CreateClassesRequest} from 'services/API/ClassApi/ClassApiModels';
import moment from 'moment';
import {Platform} from 'react-native';
import {LoaderStore} from './LoaderStore';
import {timeToUtc} from 'helpers/dateFormat';

export enum WeekDays {
  monday = 'MONDAY',
  tuesday = 'TUESDAY',
  wednesday = 'WEDNESDAY',
  thursday = 'THURSDAY',
  friday = 'FRIDAY',
  saturday = 'SATURDAY',
  sunday = 'SUNDAY',
}

export type TDay = {
  abbreviation: string;
  title: string;
  day: WeekDays;
  times: string[];
};

export interface ICreateClassStore {
  subjectData: string[];
  setSubjectData(value: string[]): void;
  subject: string;
  setSubject(value: string | Date | number): void;
  languageData: string[];
  setLanguageData(value: string[]): void;
  language: string;
  setLanguage(value: string | Date | number): void;
  groupData: string[];
  setGroupData(value: string[]): void;
  group: string;
  setGroup(value: string | Date | number): void;
  startDate: string;
  setStartDate(value: string): void;
  endDate: string;
  setEndDate(value: string): void;
  duration: number;
  setDuration(value: number): void;
  days: TDay[];
  setDays(value: TDay[]): void;
  setUpArrayOfTimes(day: string, value: string): void;
  timeEditInDay(day: string, index: number, value: string): void;
  deleteTimeFromArray(day: string, time: string): void;
  deleteDayFromArray(day: string): void;
  getClassesMetadata(): Promise<void>;
  createClass(): Promise<void>;
  day: string;
  setDay(value: string): void;
  time: string;
  setTime(value: string): void;
  cleareStore(): void;
}

export class CreateClassStore implements ICreateClassStore {
  private classApiManager = new ClassApiManager();

  loaderStore: LoaderStore;
  subjectData: string[] = [];
  subject: string = '';
  languageData: string[] = [];
  language: string = '';
  groupData: string[] = [];
  group: string = '';
  startDate: string = INITIAL_DATE;
  endDate: string = generateAWeekRangeForPicker(INITIAL_DATE);
  duration: number = 15;
  days: TDay[] = [];
  day: string = '';
  time: string = '';

  constructor(loaderStore: LoaderStore) {
    this.loaderStore = loaderStore;

    makeAutoObservable(this);
  }

  setSubjectData(value: string[]): void {
    this.subjectData = value;
  }

  setLanguageData(value: string[]): void {
    this.languageData = value;
  }

  setGroupData(value: string[]): void {
    this.groupData = value;
  }

  setSubject(value: string): void {
    this.subject = value;
  }

  setLanguage(value: string): void {
    this.language = value;
  }

  setGroup(value: string): void {
    this.group = value;
  }

  setStartDate(value: string): void {
    this.startDate = value;

    const endDate = generateAWeekRangeForPicker(value);
    if (moment(endDate) > moment(this.endDate)) {
      this.setEndDate(endDate);
    }
  }

  setEndDate(value: string): void {
    this.endDate = value;
  }

  setDuration(value: number): void {
    this.duration = value;
  }

  setDays(value: TDay[]): void {
    this.days = value;
  }

  setUpArrayOfTimes(day: string, value: string): void {
    const newSelectedDays = this.days.map(item => {
      if (item.day === day) {
        const times = item.times;

        if (Platform.OS === 'web') {
          times.push(value);
        }

        if (!times.includes(value)) {
          times.push(value);
        }

        return {...item, times: times};
      }

      return item;
    });

    this.setDays(newSelectedDays);
  }

  timeEditInDay(day: string, index: number, value: string): void {
    const newSelectedDays = this.days.map(item => {
      if (item.day === day) {
        const times = item.times;

        times[index] = value;
      }

      return item;
    });

    this.setDays(newSelectedDays);
  }

  deleteTimeFromArray(day: string, time: string): void {
    const newSelectedDays = this.days
      .map(item => {
        if (item.day === day) {
          const times = item.times;
          const newTimesArr = times.filter(subItem => subItem !== time);
          return {...item, times: newTimesArr};
        }

        return item;
      })
      .filter(item => item.times.length !== 0);

    this.setDays(newSelectedDays);
  }

  deleteDayFromArray(day: string): void {
    const newDaysArr = this.days.filter(item => item.day !== day);

    this.setDays(newDaysArr);
  }

  setDay(value: string): void {
    this.day = value;
  }

  setTime(value: string): void {
    this.time = value;
  }

  async getClassesMetadata() {
    this.loaderStore.startLoading();

    try {
      const {languages, subjects, groups} =
        await this.classApiManager.getClassesMetadata();

      this.setSubjectData(subjects);
      this.setLanguageData(languages);
      this.setGroupData(groups);
      this.setLanguage(halfValueOfArray(languages));
      this.setSubject(halfValueOfArray(subjects));
      this.setGroup(halfValueOfArray(groups));

      this.loaderStore.stopLoading();
    } catch (error) {
      console.log(error);
      this.loaderStore.stopLoading();
    }
  }

  async createClass() {
    this.loaderStore.startLoading();

    try {
      const adaptedDays = this.days.map(item => {
        return {
          day: item.day,
          times: item.times.map(time => timeToUtc(time)),
        };
      });

      const classObject: CreateClassesRequest = {
        subject: this.subject,
        language: this.language,
        group: this.group,
        startDate: this.startDate,
        endDate: this.endDate,
        duration: this.duration,
        days: adaptedDays,
      };

      await this.classApiManager.createClass(classObject);

      this.cleareStore();
      this.loaderStore.stopLoading();
    } catch (error) {
      console.log(error);
      this.loaderStore.stopLoading();
    }
  }

  cleareStore(): void {
    this.setSubject(halfValueOfArray(this.subjectData));
    this.setLanguage(halfValueOfArray(this.languageData));
    this.setGroup(halfValueOfArray(this.groupData));
    this.setStartDate(INITIAL_DATE);
    this.setEndDate(generateAWeekRangeForPicker(INITIAL_DATE));
    this.setDuration(15);
    this.setDays([]);
    this.setDay('');
    this.setTime('');
  }
}
